/**
 * Renders a `{ sv, en }` content field in the active language (spec §4.8).
 * Markdown by default, because virtually every content string is Markdown.
 */
import type { Localized as LocalizedValue } from "@shared/types";
import { useLang } from "@client/i18n";
import { Markdown, type MarkdownProps } from "./Markdown";

export interface LocalizedProps {
  value: LocalizedValue | undefined | null;
  /**
   * `markdown` (default) renders KaTeX and tables; `text` renders a plain
   * string — use it only for a `title`/`alt`-like slot that cannot hold
   * elements, never for a string a student reads (`$\lambda$` would show
   * through). `variant="inline"` is the one-line form.
   */
  as?: "markdown" | "text";
  variant?: MarkdownProps["variant"];
  className?: string;
}

/** The raw string for the active language — for titles, `alt` and aria labels. */
export function useLocalizedText(): (value: LocalizedValue | undefined | null) => string {
  const { lang } = useLang();
  return (value) => (value ? (value[lang] ?? value.sv) : "");
}

export function Localized({ value, as = "markdown", variant, className }: LocalizedProps) {
  const { lang } = useLang();
  if (!value) return null;
  const text = value[lang] ?? value.sv;
  if (as === "text") return <span className={className}>{text}</span>;
  return (
    <Markdown className={className} {...(variant ? { variant } : {})}>
      {text}
    </Markdown>
  );
}
